import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Info as InfoIcon,
  Tune as ConfigureIcon,
  DeleteOutline as ResetIcon,
} from "@mui/icons-material";
import {
  SettingDefinitionDto,
  SettingKey,
  SettingValueType,
} from "@swagger/typescript-fetch-goflyer";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import {
  formatSettingKey,
  getDisplayValue,
  getTypeColor,
} from "../../../utils/AdminSettings/utils";
import { SettingEditField } from "./SettingEditField";
import { MuiConfirmationModal } from "../MuiConfirmationModal";

interface MySettingCardProps {
  override: SettingDefinitionDto;
  onSave: (
    category: string,
    settingKey: string,
    newValue: string,
    valueType: SettingValueType,
  ) => Promise<void> | void;
  onReset: (category: string, settingKey: string) => Promise<void> | void;
  readonly?: boolean;
}

export const MySettingCard = ({
  override,
  onSave,
  onReset,
  readonly,
}: MySettingCardProps) => {
  const { t } = useTranslation();
  const { setting, value } = override;
  const category = String(setting.category);
  const settingKey = String(setting.settingKey);
  const settingTestId = `${category}-${settingKey}`
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .toLowerCase();
  const typeStr = setting.valueType;
  const typeColor = getTypeColor(typeStr);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleOpenEdit = () => {
    setEditValue(getDisplayValue(value, typeStr));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue("");
  };

  const handleSaveClick = async () => {
    try {
      setIsSaving(true);
      await onSave(category, settingKey, editValue, typeStr);
      setIsEditing(false);
    } catch (e) {
      // Handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetClick = async () => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmReset = async () => {
    try {
      setIsResetting(true);
      await onReset(category, settingKey);
    } catch (e) {
      // Handled by parent
    } finally {
      setIsResetting(false);
      setIsConfirmModalOpen(false);
    }
  };

  return (
    <Card
      data-testid={`admin-user-settings-my-card-${settingTestId}`}
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid rgba(245, 130, 32, 0.3)",
        overflow: "hidden",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          borderColor: "#f58220",
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#111827", fontSize: "16px" }}
              >
                {formatSettingKey(String(setting.settingKey))}
              </Typography>
              <Chip
                label={typeStr}
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "11px",
                  fontWeight: 600,
                  bgcolor: typeColor + "15",
                  color: typeColor,
                  border: `1px solid ${typeColor}30`,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1,
                  py: 0.25,
                  bgcolor: "#f3f4f6",
                  borderRadius: "6px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    color: "#4b5563",
                    fontWeight: 500,
                  }}
                >
                  {setting.category} / {setting.settingKey}
                </Typography>
              </Box>
            </Box>

            {setting.description && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                  mb: 1.5,
                  mt: 0.5,
                }}
              >
                <InfoIcon
                  sx={{
                    fontSize: 16,
                    color: "#6b7280",
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#4b5563", lineHeight: 1.5, fontSize: "14px" }}
                >
                  {setting.description}
                </Typography>
              </Box>
            )}

            {isEditing ? (
              <Box sx={{ mt: 1 }}>
                <SettingEditField
                  valueType={typeStr}
                  valueSchema={String(setting.valueSchema)}
                  settingKey={setting.settingKey as SettingKey}
                  editValue={editValue}
                  isSaving={isSaving}
                  onEditValueChange={setEditValue}
                  onSave={handleSaveClick}
                  onCancel={handleCancel}
                  testIdPrefix={`admin-user-settings-my-edit-${settingTestId}`}
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    bgcolor: "rgba(245, 130, 32, 0.05)",
                    borderRadius: "8px",
                    border: "2px solid rgba(245, 130, 32, 0.2)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "#6b7280", fontSize: "12px" }}
                  >
                    {t(messages.Effective_Value())}:
                  </Typography>
                  <Box
                    component={
                      typeStr === SettingValueType.Json ? "pre" : "span"
                    }
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: typeStr === SettingValueType.Json ? 400 : 700,
                      color: "#f58220",
                      m: 0,
                      whiteSpace:
                        typeStr === SettingValueType.Json
                          ? "pre-wrap"
                          : "normal",
                      wordBreak:
                        typeStr === SettingValueType.Json
                          ? "break-word"
                          : "normal",
                      maxHeight:
                        typeStr === SettingValueType.Json ? "250px" : "none",
                      overflowY:
                        typeStr === SettingValueType.Json ? "auto" : "visible",
                      fontSize:
                        typeStr === SettingValueType.Json ? "11px" : "inherit",
                    }}
                  >
                    {getDisplayValue(value, typeStr) || (
                      <Box
                        component="span"
                        sx={{ color: "#9ca3af", fontStyle: "italic" }}
                      >
                        Empty
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    bgcolor: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "#9ca3af", fontSize: "12px" }}
                  >
                    {t(messages.Default_Value())}:
                  </Typography>
                  <Box
                    component={
                      typeStr === SettingValueType.Json ? "pre" : "span"
                    }
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: typeStr === SettingValueType.Json ? 400 : 600,
                      color: "#9ca3af",
                      m: 0,
                      whiteSpace:
                        typeStr === SettingValueType.Json
                          ? "pre-wrap"
                          : "normal",
                      wordBreak:
                        typeStr === SettingValueType.Json
                          ? "break-word"
                          : "normal",
                      maxHeight:
                        typeStr === SettingValueType.Json ? "250px" : "none",
                      overflowY:
                        typeStr === SettingValueType.Json ? "auto" : "visible",
                      fontSize:
                        typeStr === SettingValueType.Json ? "11px" : "inherit",
                    }}
                  >
                    {getDisplayValue(setting.defaultValue, typeStr) || (
                      <Box component="span" sx={{ fontStyle: "italic" }}>
                        Empty
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {!isEditing && !readonly && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Tooltip title={t(messages.Configure())} placement="left">
                <IconButton
                  data-testid={`admin-user-settings-my-configure-${settingTestId}`}
                  size="small"
                  onClick={handleOpenEdit}
                  sx={{
                    bgcolor: "rgba(245, 130, 32, 0.1)",
                    color: "#f58220",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "rgba(245, 130, 32, 0.2)" },
                  }}
                >
                  <ConfigureIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t(messages.Delete())} placement="left">
                <IconButton
                  data-testid={`admin-user-settings-my-reset-${settingTestId}`}
                  size="small"
                  onClick={handleResetClick}
                  disabled={isResetting}
                  sx={{
                    bgcolor: "#f3f4f6",
                    color: "#6b7280",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#e5e7eb" },
                  }}
                >
                  <ResetIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <MuiConfirmationModal
            open={isConfirmModalOpen}
            handleCancel={() => setIsConfirmModalOpen(false)}
            handleConfirm={handleConfirmReset}
            title={t(messages.Are_you_sure_you_want_to_delete_This())}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
